const LOCALE = 'en-GB';

const DURATION_UNITS = { day: 'd', hour: 'h', minute: 'min' } as const;

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const BYTE_STEP = 1024;

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

const DATE_TIME = new Intl.DateTimeFormat(LOCALE, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const DECIMAL = new Intl.NumberFormat(LOCALE);

const FIXED = new Map<number, Intl.NumberFormat>();

function fixedFormat(digits: number): Intl.NumberFormat {
  const existing = FIXED.get(digits);
  if (existing !== undefined) return existing;
  const created = new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  FIXED.set(digits, created);
  return created;
}

export function formatTimestamp(millis: number): string {
  if (!Number.isSafeInteger(millis) || millis < 0) return '—';
  return DATE_TIME.format(new Date(millis));
}

export function formatCount(value: number): string {
  return DECIMAL.format(value);
}

export function formatDuration(millis: number): string {
  if (!Number.isFinite(millis) || millis < 0) return '—';
  if (millis < MINUTE) return `${DECIMAL.format(Math.round(millis / 1000))} s`;
  const days = Math.floor(millis / DAY);
  const hours = Math.floor((millis % DAY) / HOUR);
  const minutes = Math.floor((millis % HOUR) / MINUTE);
  const parts: Array<string> = [];
  if (days > 0) parts.push(`${DECIMAL.format(days)} ${DURATION_UNITS.day}`);
  if (hours > 0) parts.push(`${DECIMAL.format(hours)} ${DURATION_UNITS.hour}`);
  if (minutes > 0 && days === 0) parts.push(`${DECIMAL.format(minutes)} ${DURATION_UNITS.minute}`);
  if (parts.length === 0) return `${DECIMAL.format(1)} ${DURATION_UNITS.minute}`;
  return parts.join(' ');
}

export function remainingMillis(expiresAt: number | null, reference: number): number | null {
  if (expiresAt === null) return null;
  return Math.max(expiresAt - reference, 0);
}

export function formatBytes(value: number): string {
  if (!Number.isSafeInteger(value) || value < 0) return '—';
  if (value < BYTE_STEP) return `${fixedFormat(0).format(value)} B`;
  let scaled = value / BYTE_STEP;
  let unit = 1;
  while (scaled >= BYTE_STEP && unit < BYTE_UNITS.length - 1) {
    scaled /= BYTE_STEP;
    unit += 1;
  }
  const digits = scaled < 10 ? 1 : 0;
  return `${fixedFormat(digits).format(scaled)} ${BYTE_UNITS[unit] ?? 'B'}`;
}

export function formatExtension(fileName: string): string {
  const separator = fileName.lastIndexOf('.');
  if (separator <= 0 || separator === fileName.length - 1) return '';
  return fileName.slice(separator + 1).toLowerCase();
}
